import React, { useState, useEffect, useRef } from "react";
import { Card, TextArea, Button } from "./components/UI.jsx";
import IntakeCompleteModal from "./components/IntakeCompleteModal.jsx";
import { useTranslation } from "./i18n";

// const API_BASE = "http://127.0.0.1:8001";

// --- CHAT BUBBLE COMPONENT ---
const ChatMessage = ({ role, text, widget_type, widget_data, onAction, isLatest }) => { // Props aangepast
  const isUser = role === "user";
  const hasText = typeof text === "string" ? text.trim().length > 0 : Boolean(text);
  // We disablen de widget als het NIET het laatste bericht is.
  // (Tenzij het een user message is, maar die heeft geen widgets).
  const isDisabled = !isLatest;
  
  return (
    <div className={`flex flex-col ${isUser ? "items-end" : "items-start"} mb-6 animate-fade-in-up`}>
      {/* De Tekst Bubbel */}
      {(isUser || hasText) && (
        <div 
          className={`max-w-[85%] p-4 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap shadow-sm
            ${isUser 
              ? "bg-brand-green text-white rounded-tr-none" 
              : "bg-white border border-gray-200 text-gray-800 rounded-tl-none"
            }`}
        >
          {text}
        </div>
      )}

      {/* De Widgets (Alleen bij model messages) */}
      {!isUser && widget_type === "occupation_selector" && (
        <OccupationWidget 
          data={widget_data} 
          onSelect={(uri) => onAction("select_occupation", uri)} 
          disabled={isDisabled}
        />
      )}

      {/* NIEUW: Richting-selector (kies 2 van 5 richtingen) */}
      {!isUser && widget_type === "direction_selector" && (
        <DirectionSelectorWidget
          data={widget_data}
          onSubmit={(uris) => onAction("select_directions", uris)}
          disabled={isDisabled}
        />
      )}
      
      {/* OUDE: skill_slider - nu vervangen door skills_open_answer */}
      {!isUser && widget_type === "skill_slider" && (
        <SliderWidget 
          data={widget_data} 
          onSubmit={(val) => onAction("submit_skill", val)} 
          disabled={isDisabled}
        />
      )}

      {/* Skills Wizard - 3 stappen: intro -> casus -> per-skill vragen */}
      {!isUser && widget_type === "skills_intro" && (
        <SkillsIntroWidget
          data={widget_data}
          onStart={() => onAction("skills_step", { action: "start_case" })}
          disabled={isDisabled}
        />
      )}

      {!isUser && widget_type === "skills_case" && (
        <SkillsCaseWidget
          data={widget_data}
          onStart={() => onAction("skills_step", { action: "start_questions" })}
          disabled={isDisabled}
        />
      )}

      {!isUser && widget_type === "skill_question" && (
        <SkillQuestionWidget
          text={text}
          data={widget_data}
          onSubmit={(answer) => onAction("skills_step", { action: "submit_answer", answer })}
          disabled={isDisabled}
        />
      )}

      {/* NIEUW: Constraints Selector Widget */}
      {!isUser && widget_type === "constraints_selector" && (
        <ConstraintsSelectorWidget
          data={widget_data}
          onSubmit={(constraints) => onAction("submit_constraints", constraints)}
          disabled={isDisabled}
        />
      )}
    </div>
  );
};

// --- NIEUWE WIDGETS ---

// 1. Keuzelijst voor Beroepen (Met Submit Lock)
const OccupationWidget = ({ data, onSelect, disabled }) => {
  const { t } = useTranslation();
  const [selectedUri, setSelectedUri] = useState(null);
  const [submitted, setSubmitted] = useState(false); // <--- Nieuwe state
  const [showAll, setShowAll] = useState(false);

  if (!data || data.length === 0) return null;

  if (disabled) {
    return (
      <div className="mt-3 w-full max-w-md bg-gray-50 p-4 rounded-xl border border-gray-200 opacity-60 grayscale pointer-events-none">
        <p className="text-gray-400 text-sm italic">{t('candidate_app.common.selection_unavailable')}</p>
      </div>
    );
  }

  const canShowMore = data.length > 10;
  const visibleJobs = showAll ? data : data.slice(0, 10);

  // De handler die 'lockt' na klikken
  const handleSelect = () => {
    if (selectedUri) {
      setSubmitted(true);
      onSelect(selectedUri);
    }
  };

  // Als er al gekozen is, tonen we alleen nog het resultaat (niet meer de lijst)
  if (submitted) {
    const chosenJob = data.find(job => job.occupation_uri === selectedUri);
    return (
      <div className="mt-3 w-full max-w-md bg-green-50 border border-green-100 p-4 rounded-xl flex items-center gap-3 animate-fade-in-up">
         <div className="bg-brand-green text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">✔</div>
         <div>
           <div className="text-xs text-brand-green uppercase font-bold">Geselecteerd</div>
           <div className="font-bold text-green-900">{chosenJob ? chosenJob.label : t('candidate_app.widgets.occupation.fallback_label')}</div>
         </div>
      </div>
    );
  }

  // De normale keuzelijst
  return (
    <div className="mt-3 space-y-2 w-full max-w-md">
      {visibleJobs.map((job) => {
        const isSelected = selectedUri === job.occupation_uri;
        return (
          <div
            key={job.occupation_uri}
            onClick={() => setSelectedUri(job.occupation_uri)}
            className={`
              cursor-pointer p-3 rounded-lg border transition-all text-left group
              ${isSelected ? "border-brand-green bg-green-50 ring-1 ring-brand-green" : "border-gray-200 bg-white hover:border-green-300"}
            `}
          >
            <div className="flex justify-between items-center">
              <span className={`font-bold text-sm ${isSelected ? "text-blue-900" : "text-gray-800"}`}>
                {job.label}
              </span>
              <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                {(job.score * 100).toFixed(0)}%
              </span>
            </div>
          </div>
        );
      })}
      
      <div className="pt-2">
        <Button 
          className="w-full text-sm py-2" 
          disabled={!selectedUri} 
          onClick={handleSelect} // <--- Gebruik de nieuwe handler
        >
          {t('candidate_app.widgets.occupation.choose_button')}
        </Button>
      </div>

      {canShowMore && (
        <div className="pt-1">
          <button
            type="button"
            className="w-full rounded-lg border border-[#151827]/20 bg-[#fffdf8] px-3 py-2 text-xs font-semibold text-[#151827]/75 transition hover:bg-[#f6f1e8]"
            onClick={() => setShowAll(true)}
            disabled={showAll}
          >
              {showAll ? t('candidate_app.widgets.occupation.showing_full_top') : t('candidate_app.widgets.occupation.show_top_25')}
          </button>
        </div>
      )}
    </div>
  );
};

// 2. Slider in de Chat (Verrijkt)
const SliderWidget = ({ data, onSubmit, disabled }) => {
  const { t } = useTranslation();
  const [val, setVal] = useState(Math.round((data.min + data.max) / 2));
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    setSubmitted(true);
    onSubmit(String(val));
  };

  if (submitted) {
    return (
      <div className="mt-2 text-xs text-gray-500 italic bg-gray-50 p-2 rounded inline-block border border-gray-100">
        {t('candidate_app.widgets.slider.saved', { value: val })}
      </div>
    );
  }

  // Scenario 2: Niet ingevuld, maar wel verouderd (Disabled)
  if (disabled) {
    return (
      <div className="mt-3 bg-gray-50 p-4 rounded-xl border border-gray-200 w-full max-w-md opacity-60 grayscale pointer-events-none select-none">
         <div className="flex justify-between items-start mb-2">
            <div>
               <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
              {data.parent_label || t('candidate_app.widgets.slider.skill_group')}
               </div>
               <h3 className="font-bold text-gray-500 text-lg">{data.label}</h3>
            </div>
         </div>
      </div>
    );
  }

  return (
    <div className="mt-3 bg-white p-5 rounded-xl border border-blue-100 shadow-sm w-full max-w-md relative animate-fade-in-up">
      
      {/* HEADER: Labels & Info */}
      <div className="flex justify-between items-start mb-6">
        <div className="pr-4">
           {/* Parent Groep (indien aanwezig) */}
           {data.parent_label && (
             <div className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-1">
               {data.parent_label}
             </div>
           )}
           {/* Skill Naam */}
           <h3 className="font-bold text-gray-900 text-lg leading-tight">
             {data.label}
           </h3>
        </div>

        {/* Info Icon + Tooltip */}
        {data.description && (
          <div className="relative group shrink-0">
            <div className="cursor-help text-gray-300 hover:text-blue-500 transition-colors">
               <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
               </svg>
            </div>
            
            {/* De Tooltip Popup */}
            <div className="absolute right-0 top-6 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-xl z-20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto leading-relaxed">
               {data.description}
               {/* Pijl voor tooltip */}
               <div className="absolute -top-1 right-1 w-2 h-2 bg-gray-800 rotate-45"></div>
            </div>
          </div>
        )}
      </div>

      {/* SLIDER GEDEELTE */}
      <div className="mb-2">
        <div className="flex justify-between text-xs text-gray-400 font-medium mb-2 uppercase tracking-wide">
            <span>{t('candidate_app.widgets.slider.min_label', { value: data.min })}</span>
            <span>{t('candidate_app.widgets.slider.max_label', { value: data.max })}</span>
        </div>
        <input
          type="range"
          className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-brand-green hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green/30"
          min={data.min} 
          max={data.max} 
          value={val}
          onChange={(e) => setVal(Number(e.target.value))}
        />
      </div>

      {/* FOOTER: Score & Knop */}
      <div className="flex justify-between items-center mt-5 pt-3 border-t border-gray-50">
        <div className="flex items-baseline gap-1">
           <span className="font-bold text-2xl text-brand-green">{val}</span>
           <span className="text-gray-400 text-sm font-medium">/ 5</span>
        </div>
        <Button className="!py-2 !px-6 text-sm shadow-sm hover:shadow-md" onClick={handleSubmit}>
          {t('common.confirm')}
        </Button>
      </div>
    </div>
  );
};

// 2b. Richting-selector: kies exact 2 van 5 ISCO-clusters (nieuwe flow 2026-07)
const DirectionSelectorWidget = ({ data, onSubmit, disabled }) => {
  const { t } = useTranslation();
  const [selectedUris, setSelectedUris] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  if (!data || data.length === 0) return null;

  if (disabled) {
    return (
      <div className="mt-3 w-full max-w-3xl bg-gray-50 p-4 rounded-xl border border-gray-200 opacity-60 grayscale pointer-events-none">
        <p className="text-gray-400 text-sm italic">{t('candidate_app.common.selection_unavailable')}</p>
      </div>
    );
  }

  const toggleSelect = (uri) => {
    setSelectedUris((prev) => {
      if (prev.includes(uri)) return prev.filter((u) => u !== uri);
      if (prev.length >= 2) return prev; // max 2
      return [...prev, uri];
    });
  };

  const handleSubmit = () => {
    if (selectedUris.length !== 2) return;
    setSubmitted(true);
    onSubmit(selectedUris);
  };

  if (submitted) {
    const chosen = data.filter((d) => selectedUris.includes(d.direction_uri));
    return (
      <div className="mt-3 w-full max-w-3xl bg-green-50 border border-green-100 p-4 rounded-xl animate-fade-in-up">
        <div className="text-xs text-brand-green uppercase font-bold mb-2">{t('candidate_app.widgets.direction.selected_title')}</div>
        <div className="flex flex-wrap gap-2">
          {chosen.map((c) => (
            <span key={c.direction_uri} className="px-3 py-1 bg-white border border-brand-green rounded-full text-sm font-bold text-brand-green">
              {c.readable_label}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 w-full max-w-3xl space-y-3">
      <div className="text-xs text-gray-500 mb-1 font-semibold uppercase tracking-wide">
        {t('candidate_app.widgets.direction.pick_two', { selected: selectedUris.length })}
      </div>

      {data.map((dir) => {
        const isSelected = selectedUris.includes(dir.direction_uri);
        const canSelect = isSelected || selectedUris.length < 2;

        return (
          <div
            key={dir.direction_uri}
            className={`
              rounded-xl border-2 p-4 transition-all
              ${isSelected ? "border-brand-green bg-green-50 ring-1 ring-brand-green" : "border-gray-200 bg-white"}
              ${!canSelect && !isSelected ? "opacity-50" : ""}
            `}
          >
            {/* Header: label + score + select */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className={`font-bold text-base ${isSelected ? "text-brand-green" : "text-gray-900"}`}>
                    {dir.readable_label}
                  </h3>
                  <span className="text-[10px] font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    {typeof dir.direction_score === "number" ? dir.direction_score.toFixed(1) : ""}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{dir.isco_label}</div>
              </div>
              <button
                onClick={() => canSelect && toggleSelect(dir.direction_uri)}
                disabled={!canSelect}
                className={`
                  shrink-0 w-24 py-1.5 text-xs font-bold rounded-lg border-2 transition-colors
                  ${isSelected
                    ? "bg-brand-green text-white border-brand-green"
                    : canSelect
                      ? "bg-white text-gray-700 border-gray-300 hover:border-brand-green"
                      : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"}
                `}
              >
                {isSelected ? t('candidate_app.widgets.direction.chosen_button') : t('candidate_app.widgets.direction.choose_button')}
              </button>
            </div>

            {/* Fits + less_fits */}
            {(dir.fits && dir.fits.length > 0) || dir.less_fits ? (
              <div className="space-y-1.5 mb-2">
                {dir.fits && dir.fits.map((fit, i) => (
                  <div key={i} className="text-sm text-gray-700 flex gap-2">
                    <span className="text-brand-green shrink-0">✓</span>
                    <span>{fit}</span>
                  </div>
                ))}
                {dir.less_fits && (
                  <div className="text-sm text-gray-500 flex gap-2">
                    <span className="text-amber-500 shrink-0">!</span>
                    <span>{dir.less_fits}</span>
                  </div>
                )}
              </div>
            ) : null}

            {/* Best passende ESCO-beroepen binnen deze richting (direct zichtbaar) */}
            {dir.occupations && dir.occupations.length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                  {t('candidate_app.widgets.direction.example_occupations_title')}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {dir.occupations.map((occ) => (
                    <div
                      key={occ.occupation_uri}
                      className="flex justify-between items-center px-2.5 py-1.5 bg-gray-50 border border-gray-100 rounded-md text-xs"
                    >
                      <span className="text-gray-700">{occ.label}</span>
                      <span className="text-gray-400 font-mono">{(occ.score * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="pt-2 sticky bottom-0">
        <Button
          className="w-full text-sm py-2.5"
          disabled={selectedUris.length !== 2}
          onClick={handleSubmit}
        >
          {selectedUris.length === 2 ? t('candidate_app.widgets.direction.confirm_selection') : t('candidate_app.widgets.direction.remaining', { count: 2 - selectedUris.length })}
        </Button>
      </div>
    </div>
  );
};

// 3. Skills Wizard widgets — intro, casus, en per-skill vraag (vervangen oude open-answer)
const SkillsIntroWidget = ({ data, onStart, disabled }) => {
  const { t } = useTranslation();
  if (disabled) {
    return (
      <div className="mt-3 w-full max-w-lg bg-gray-50 p-4 rounded-xl border border-gray-200 opacity-60 grayscale pointer-events-none">
        <p className="text-gray-400 text-sm italic">{t('candidate_app.common.step_unavailable')}</p>
      </div>
    );
  }
  return (
    <div className="mt-3 w-full max-w-lg bg-white p-5 rounded-xl border border-green-100 shadow-sm animate-fade-in-up">
      <div className="text-xs uppercase font-bold text-brand-green mb-2">{t('candidate_app.widgets.skills_intro.title')}</div>
      <div className="text-sm text-gray-600 mb-4">
        {t('candidate_app.widgets.skills_intro.body', { total: data?.total || t('candidate_app.widgets.skills_intro.some_count') })}
      </div>
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        {t('candidate_app.widgets.skills_intro.attention_note')}
      </div>
      <Button className="!py-2 !px-6 text-sm" onClick={onStart}>
        {t('candidate_app.widgets.skills_intro.start_case')}
      </Button>
    </div>
  );
};

const SkillsCaseWidget = ({ data, onStart, disabled }) => {
  const { t } = useTranslation();
  if (disabled) {
    return (
      <div className="mt-3 w-full max-w-lg bg-gray-50 p-4 rounded-xl border border-gray-200 opacity-60 grayscale pointer-events-none">
        <p className="text-gray-400 text-sm italic">{t('candidate_app.common.step_unavailable')}</p>
      </div>
    );
  }
  return (
    <div className="mt-3 w-full max-w-lg bg-[#fffdf8] p-5 rounded-xl border border-[#151827]/15 shadow-sm animate-fade-in-up">
      <div className="text-xs uppercase font-bold text-[#151827]/70 mb-2">{t('candidate_app.widgets.skills_case.title')}</div>
      <div className="text-sm text-gray-600 mb-4">
        {t('candidate_app.widgets.skills_case.body', { total: data?.total || t('candidate_app.widgets.skills_case.default_total') })}
      </div>
      <Button className="!py-2 !px-6 text-sm" onClick={onStart}>
        {t('candidate_app.widgets.skills_case.start_questions')}
      </Button>
    </div>
  );
};

const SkillQuestionWidget = ({ text, data, onSubmit, disabled }) => {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (answer.trim().length < 30) return;
    setSubmitted(true);
    onSubmit(answer.trim());
  };

  if (submitted) {
    return (
      <div className="mt-3 w-full max-w-md bg-green-50 border border-green-100 p-4 rounded-xl flex items-center gap-3 animate-fade-in-up">
        <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">✔</div>
        <div>
          <div className="text-xs text-green-600 uppercase font-bold">{t('candidate_app.widgets.skill_question.saved_title')}</div>
          <div className="text-sm text-green-800 mt-1">{t('candidate_app.widgets.skill_question.saved_progress', { index: data?.index || 1, total: data?.total || 1 })}</div>
        </div>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="mt-3 w-full max-w-lg bg-gray-50 p-4 rounded-xl border border-gray-200 opacity-60 grayscale pointer-events-none">
        <p className="text-gray-400 text-sm italic">{t('candidate_app.common.question_unavailable')}</p>
      </div>
    );
  }

  return (
    <div className="mt-3 w-full max-w-lg bg-white p-5 rounded-xl border border-green-100 shadow-sm animate-fade-in-up">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase font-bold text-brand-green">{t('candidate_app.widgets.skill_question.progress', { index: data?.index || 1, total: data?.total || 1 })}</div>
        <div className="text-xs text-gray-500 max-w-[60%] truncate text-right" title={(data?.skill_labels || []).join(" & ") || data?.skill_label || ""}>
          {(data?.skill_labels && data.skill_labels.length > 0)
            ? data.skill_labels.join(" & ")
            : (data?.skill_label || "")}
        </div>
      </div>
      {data?.cluster_label && (
        <div className="mb-3 inline-block rounded-full bg-brand-green/10 px-2.5 py-1 text-[11px] font-semibold text-brand-green">
          {t('candidate_app.widgets.skill_question.cluster_label', { cluster: data.cluster_label })}
        </div>
      )}
      <textarea
        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-green resize-none text-sm min-h-[140px]"
        placeholder={t('candidate_app.widgets.skill_question.placeholder')}
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        aria-label={text || t('candidate_app.widgets.skill_question.aria_label')}
      />
      <div className="flex justify-between items-center mt-3">
        <span className={`text-xs ${answer.length < 30 ? "text-orange-500" : "text-gray-400"}`}>
          {answer.length < 30
            ? t('candidate_app.widgets.skill_question.characters_needed', { count: 30 - answer.length })
            : t('candidate_app.widgets.skill_question.character_count', { count: answer.length })}
        </span>
        <Button className="!py-2 !px-6 text-sm" onClick={handleSubmit} disabled={answer.length < 30}>
          {t('candidate_app.widgets.skill_question.submit_answer')}
        </Button>
      </div>
    </div>
  );
};

// 4. NIEUW: Constraints Selector Widget (Aan einde van skills flow)
const ConstraintsSelectorWidget = ({ data, onSubmit, disabled }) => {
  const { t } = useTranslation();
  // importance: 1 = niet belangrijk, 2 = enigszins belangrijk, 3 = erg belangrijk
  const [importance, setImportance] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const options = data?.options || [];

  const setConstraintImportance = (key, level) => {
    setImportance(prev => ({ ...prev, [key]: level }));
  };

  const handleSubmit = () => {
    setSubmitted(true);
    // Send importance levels (default to 1 if not set)
    const finalImportance = {};
    options.forEach(opt => {
      finalImportance[opt.key] = importance[opt.key] || 1;
    });
    onSubmit(finalImportance);
  };

  // Na submit: toon bevestiging
  if (submitted) {
    const importantCount = Object.values(importance).filter(v => v >= 2).length;
    return (
      <div className="mt-3 w-full max-w-md bg-green-50 border border-green-100 p-4 rounded-xl flex items-center gap-3 animate-fade-in-up">
        <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">✔</div>
        <div>
          <div className="text-xs text-green-600 uppercase font-bold">{t('candidate_app.widgets.constraints.saved_title')}</div>
          <div className="text-sm text-green-800 mt-1">{t('candidate_app.widgets.constraints.saved_count', { count: importantCount })}</div>
        </div>
      </div>
    );
  }

  // Disabled state
  if (disabled) {
    return (
      <div className="mt-3 w-full max-w-lg bg-gray-50 p-4 rounded-xl border border-gray-200 opacity-60 grayscale pointer-events-none">
        <p className="text-gray-400 text-sm italic">{t('candidate_app.common.selection_unavailable')}</p>
      </div>
    );
  }

  // Groepeer opties per categorie
  const grouped = {};
  options.forEach(opt => {
    const cat = opt.category || "Overig";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(opt);
  });

  const importanceLevels = [
    { level: 1, label: t('candidate_app.widgets.constraints.level_1'), shortLabel: "1", color: "gray" },
    { level: 2, label: t('candidate_app.widgets.constraints.level_2'), shortLabel: "2", color: "yellow" },
    { level: 3, label: t('candidate_app.widgets.constraints.level_3'), shortLabel: "3", color: "green" }
  ];

  return (
    <div className="mt-3 w-full max-w-3xl bg-white p-5 rounded-xl border border-green-100 shadow-sm animate-fade-in-up">
      <div className="text-sm text-gray-600 mb-2">
        {t('candidate_app.widgets.constraints.intro')}
      </div>
      <div className="flex gap-4 text-xs text-gray-500 mb-4 pl-1">
        <span><strong>1</strong> = {t('candidate_app.widgets.constraints.level_1')}</span>
        <span><strong>2</strong> = {t('candidate_app.widgets.constraints.level_2')}</span>
        <span><strong>3</strong> = {t('candidate_app.widgets.constraints.level_3')}</span>
      </div>
      
      <div className="space-y-4">
        {Object.entries(grouped).map(([category, categoryOptions]) => (
          <div key={category}>
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{category}</div>
            <div className="space-y-2">
              {categoryOptions.map((opt) => {
                const currentLevel = importance[opt.key] || 1;
                const primaryText = opt.description || opt.label;
                return (
                  <div 
                    key={opt.key}
                    className="p-3 rounded-lg border border-gray-200 bg-gray-50 flex justify-between items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 leading-relaxed">{primaryText}</p>
                      {opt.description && opt.label && (
                        <p className="mt-1 text-[11px] uppercase tracking-wide text-gray-400">{opt.label}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {importanceLevels.map(({ level, label, shortLabel, color }) => {
                        const isActive = currentLevel === level;
                        const buttonColors = {
                          gray: isActive ? "bg-gray-400 text-white border-gray-400" : "bg-white text-gray-500 border-gray-300 hover:border-gray-400",
                          yellow: isActive ? "bg-yellow-500 text-white border-yellow-500" : "bg-white text-yellow-600 border-gray-300 hover:border-yellow-400",
                          green: isActive ? "bg-brand-green text-white border-brand-green" : "bg-white text-green-600 border-gray-300 hover:border-green-400"
                        };
                        return (
                          <button
                            key={level}
                            onClick={() => setConstraintImportance(opt.key, level)}
                            title={label}
                            className={`
                              w-8 h-8 rounded-lg border-2 font-bold text-sm transition-all
                              ${buttonColors[color]}
                            `}
                          >
                            {shortLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-5 pt-4 border-t border-gray-100">
        <Button className="!py-3 !px-8 text-sm shadow-md" onClick={handleSubmit}>
          {t('candidate_app.widgets.constraints.confirm_preferences')}
        </Button>
      </div>
    </div>
  );
};

// Voeg props toe voor de data die vanuit MainPlatform komt
export default function CandidateApp({ userId, initialPersonalia, apiBase, onBackToDashboard, onBackToHome }) { 
  const { t } = useTranslation();

  // State setup
  const [loadingState, setLoadingState] = useState(true); // Nieuwe laad-state
  const [currentPhase, setCurrentPhase] = useState("START"); // Nieuwe fase-logica: START, IDENTITY, AMBITION, OCCUPATION, SKILLS, CONSTRAINTS, COMPLETED
  const [activeModule, setActiveModule] = useState("identity");
  const [chatHistory, setChatHistory] = useState([]);
  const [activeRevision, setActiveRevision] = useState(null);
  
  const candidateId = userId; 
  const [personalia] = useState(initialPersonalia || {});

  // State variabelen - veel oude state is verwijderd omdat alles nu via chat/widgets gaat
  const [busy, setBusy] = useState(false);

  // Rapport & Reflectie (gebruikt in finalReport binnen chat)
  const [reflectionText, setReflectionText] = useState("");
  const [learningGoals, setLearningGoals] = useState("");
  const [roleFitness, setRoleFitness] = useState(5);
  const reflectionSubmitting = false;
  const [reflectionSubmitted, setReflectionSubmitted] = useState(false);

  // interview state
  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const finalReport = null;
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);
  const messagesEndRef = useRef(null);
  
  // State voor de TwinUpdatePayload
  const [twinReviewText, setTwinReviewText] = useState("");
  // KISS: constraints en non_negotiables verwijderd - voegen weinig toe in de praktijk
  // const [twinConstraints, setTwinConstraints] = useState({});
  // const [twinNonNegotiables, setTwinNonNegotiables] = useState([]);
  // const [newNonNegotiableInput, setNewNonNegotiableInput] = useState("");

  // DEPRECATED: Constraints gaan nu via ConstraintsSelectorWidget in de chat
  // const [constraintOptions, setConstraintOptions] = useState([]);
  // const [selectedConstraints, setSelectedConstraints] = useState({});
  // const [ambitionConfirmed, setAmbitionConfirmed] = useState(false);

  // Auto-scroll effect
  useEffect(() => {
    // Scroll naar beneden bij elk nieuw bericht in chat fases
    if (["IDENTITY", "AMBITION", "OCCUPATION", "SKILLS", "CONSTRAINTS"].includes(currentPhase)) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory, isTyping, currentPhase]);

  // --- CHAT FUNCTIES ---
  
  // Functie om chat te starten (wordt aangeroepen na Personalia)
  async function startInterview() {
    setBusy(true);
    try {
      const res = await postJSON("/api/interview/start", {});
      // De backend geeft { message: "...", history: [...] }
      setChatHistory([{ role: "model", text: res.message }]);
      setActiveModule("identity");
      setCurrentPhase("IDENTITY"); // Start bij de identity fase
    } catch (e) {
      alert(t('candidate_app.errors.start_interview', { message: e.message }));
    } finally {
      setBusy(false);
    }
  }


  async function handleConfirmTwin() {
    setBusy(true);
    try {
        const payload = {
            narrative_profile: twinReviewText
            // KISS: constraints en non_negotiables verwijderd
            // constraints: twinConstraints,
            // non_negotiables: twinNonNegotiables
        };

        await postJSON("/api/candidate/confirm-twin", payload);
        
        if (activeRevision?.section === "IDENTITY") {
            finishRevision();
            return;
        }

        // Start de ambition module
        await startAmbitionModule();

    } catch (e) {
      alert(t('candidate_app.errors.save_failed', { message: e.message }));
    } finally {
        setBusy(false);
    }
  }  

  // KISS: constraints en non_negotiables functies verwijderd - voegen weinig toe in de praktijk
  // const toggleTwinConstraint = (key) => {
  //   setTwinConstraints(prev => ({
  //       ...prev,
  //       [key]: !prev[key]
  //   }));
  // };
  // const removeNonNegotiable = (index) => {
  //   setTwinNonNegotiables(prev => prev.filter((_, i) => i !== index));
  // };
  // const addNonNegotiable = () => {
  //   const val = newNonNegotiableInput.trim();
  //   if (!val) return;
  //   if (twinNonNegotiables.includes(val)) return;
  //   setTwinNonNegotiables(prev => [...prev, val]);
  //   setNewNonNegotiableInput("");
  // };


  // Start het ambition interview (Fase 2)
  async function startAmbitionModule() {
    setBusy(true);
    try {
        const res = await postJSON("/api/ambition/start", {});
        
        // Voeg het openingsbericht toe aan de chat
        setChatHistory(prev => [...prev, { role: "model", text: res.message }]);
        
        // Switch naar ambition module
        setActiveModule("ambition");
        setCurrentPhase("AMBITION"); // Update progress stepper
    } catch (e) {
        console.error(e);
      alert(t('candidate_app.errors.start_ambition_failed'));
    } finally {
        setBusy(false);
    }
  }

  // // Deze functie roep je aan als Fase 1 klaar is
  // async function startWorkModule() {
  //   setBusy(true);
  //   try {
  //       // Roep de NIEUWE start endpoint aan
  //       // Dit kopieert aan de backend kant de historie en bereidt de widgets voor
  //       const res = await postJSON("/api/ambition/start", {});
        
  //       // Voeg het antwoord van de workmodule toe aan de chat
  //       setChatHistory(prev => [...prev, { role: "model", text: res.message }]);
        
  //       // BELANGRIJK: Switch de 'router' naar de workmodule
  //       setActiveModule("work"); 
  //   } catch (e) {
  //       console.error(e);
  //       alert("Kon workmodule niet starten");
  //   } finally {
  //       setBusy(false);
  //   }
  // }

  // --- NIEUWE CHAT LOGICA ---
  async function handleChatAction(actionType, payload = null) {
    // 1. UI Updates (Optimistic)
    if (actionType === "message") {
      const text = payload.trim();
      if (!text) return;
      setChatHistory(prev => [...prev, { role: "user", text }]);
      setUserInput("");
    }
    
    setIsTyping(true);

    try {
      // === SPECIALE ACTIES (Widgets) ===
      
      // Occupation selectie → nieuwe skills module endpoint
      if (actionType === "select_occupation") {
        const res = await postJSON("/api/esco/submit-occupation", { occupation_uri: payload });
        if (res.ok) {
          setChatHistory(res.history || []);
          setActiveModule("skills");
          setCurrentPhase("SKILLS"); // Update progress stepper
          console.log("Occupation gekozen, skills vraag gegenereerd");
        }
        setIsTyping(false);
        return;
      }

      // Richting-selectie (nieuwe flow 2026-07): kandidaat kiest 2 richtingen
      if (actionType === "select_directions") {
        const res = await postJSON("/api/esco/submit-directions", { chosen_direction_uris: payload });
        if (res.ok) {
          setChatHistory(res.history || []);
          setActiveModule("skills");
          setCurrentPhase("SKILLS");
          console.log("Richtingen gekozen:", res.chosen_directions);
        }
        setIsTyping(false);
        return;
      }

      // Skills wizard stap → state machine endpoint
      if (actionType === "skills_step") {
        const res = await postJSON("/api/esco/skills-step", payload || {});
        if (res.ok) {
          if (res.phase === "COMPLETED") {
            setIsTyping(false);
            finishRevision();
            return;
          }
          setChatHistory(res.history || []);
          if (res.phase === "CONSTRAINTS") {
            setActiveModule("constraints");
            setCurrentPhase("CONSTRAINTS");
            console.log("Skills afgerond, constraints widget getoond");
          } else {
            setActiveModule("skills");
            setCurrentPhase("SKILLS");
          }
        }
        setIsTyping(false);
        return;
      }

      // Constraints selectie → afronden
      if (actionType === "submit_constraints") {
        const res = await postJSON("/api/constraints/submit", { selected_constraints: payload });
        if (res.ok) {
          if (activeRevision) {
            setIsTyping(false);
            finishRevision();
            return;
          }
          setChatHistory(res.history || []);
          setCurrentPhase("COMPLETED"); // Update progress stepper
          setAssessmentCompleted(true);
          console.log("Constraints opgeslagen, intake voltooid!");
        }
        setIsTyping(false);
        return;
      }

      // === STANDAARD CHAT BERICHTEN ===
      let body = {};
      if (actionType === "message") {
        body = { message: payload };
      } else {
        body = { action: actionType, payload: payload };
      }

      // Route naar de juiste endpoint op basis van activeModule
      let endpoint;
      if (activeModule === "identity") {
          endpoint = "/api/interview/chat";
      } else if (activeModule === "ambition") {
          endpoint = "/api/ambition/chat";
      } else {
          endpoint = "/api/esco/chat";
      }
      const res = await postJSON(endpoint, body);    

      // 3. Response verwerken
      if (res.ok) {
        if (res.history) {
           setChatHistory(res.history);
        } else if (res.message) {
           setChatHistory(prev => [...prev, { role: "model", text: res.message }]);
        }
        
        console.log("Huidige fase:", res.phase); // Debugging

        // SPECIFIEKE LOGICA VOOR FASE WISSELS
        
        // Scenario A: Identity intake is klaar (backend stuurt done: true)
        if (activeModule === "identity" && res.done) {
             console.log("Identity intake klaar. Overschakelen naar Review...");
             
             const twin = res.digital_twin; // Dit is het DigitalTwin object uit individual.py
             
             // 1. Narrative Profile (Tekst)
             setTwinReviewText(twin.narrative_profile || "");

             // KISS: constraints en non_negotiables verwijderd - voegen weinig toe in de praktijk
             // setTwinConstraints(twin.constraints || {});
             // setTwinNonNegotiables(twin.non_negotiables || []);
             
             setCurrentPhase("IDENTITY_REVIEW"); // Naar apart review scherm (consistent met AMBITION_REVIEW)
             return; 
        }

        // Scenario B: Ambition interview is klaar → door naar ambition review scherm
        if (activeModule === "ambition" && res.done) {
             console.log("Ambition interview klaar. Toon ambition review...");
             
             // Haal de ambition summary uit de response
             const ambitionData = res.ambition || "";
             setTwinReviewText(ambitionData); // Hergebruik deze state voor ambition review
             
             setCurrentPhase("AMBITION_REVIEW"); // Naar apart review scherm
             return;
        }

        // Scenario C: Intake volledig afgerond
        if (res.phase === "COMPLETED") {
             console.log("Intake compleet!");
             setCurrentPhase("COMPLETED");
             setAssessmentCompleted(true);
             return;
        }
      }
    } catch (e) {
      console.error("Chat error:", e);
      alert(t('candidate_app.errors.chat_generic', { message: e.message }));
    } finally {
      setIsTyping(false);
    }
  }

  async function postJSON(url, body) {
    const res = await fetch(
      apiBase + url + `?candidateId=${encodeURIComponent(candidateId)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || `HTTP ${res.status}`);
    }
    return res.json();
  }

  // Deze vervangt handlePersonalia volledig
  async function handleStartIntake() {
    await startInterview(); 
  }


  // Bevestig Ambitie -> Ga naar occupation selectie (terug naar step 2 met occupation widget in chat)
  // NOTE: Constraints worden nu NA de skills assessment getoond (niet meer hier)
  async function handleConfirmAmbition() {
      setBusy(true);
      try {
          const res = await postJSON("/api/candidate/confirm-ambition", twinReviewText);
          
          if (res.phase === "COMPLETED") {
              finishRevision();
              return;
          }

          // Update chat history (bevat nu occupation selector widget)
          if (res.history) {
              setChatHistory(res.history);
          }
          
          // Ga terug naar chat view waar de occupation widget staat
          setActiveModule("occupation");
          setCurrentPhase("OCCUPATION");
      } catch(e) { alert(e.message); } 
      finally { setBusy(false); }
  }

  // DEPRECATED: Constraints worden nu via ConstraintsSelectorWidget in de chat afgehandeld
  // async function handleSubmitConstraints() {
  //     setBusy(true);
  //     try {
  //         const res = await postJSON("/api/constraints/submit", {
  //             selected_constraints: selectedConstraints
  //         });
  //         
  //         // Update de hele chat history
  //         if (res.history) {
  //             setChatHistory(res.history);
  //         }
  //         
  //         setCurrentPhase("COMPLETED");
  //         setAssessmentCompleted(true);
  //     } catch(e) { alert(e.message); }
  //     finally { setBusy(false); }
  // }
  
  // DEPRECATED: Niet meer nodig - constraints gaan via widget
  // const toggleConstraint = (key) => {
  //     setSelectedConstraints(prev => ({ ...prev, [key]: !prev[key] }));
  // };


  async function submitReflection() {
    try {
      await postJSON("/api/reflection", { 
          reflectionText, 
          learningGoals,
          roleFitnessScore: roleFitness // <--- Meesturen
      });
      setReflectionSubmitted(true);
    } catch(e) { alert(e.message); }
  }

// === HYDRATION EFFECT ===
  useEffect(() => {
    async function restoreSession() {
      try {
        const res = await fetch(`${apiBase}/api/candidate/status?candidateId=${encodeURIComponent(userId)}`);
        const status = await res.json();
        
        if (!status.exists) {
            // Geen sessie gevonden - begin bij START
            setCurrentPhase("START");
            if(initialPersonalia?.name) {
                 // Als we personalia al hebben, start interview direct
                 startInterview(true);
            }
        } else {
            // == HERSTEL DE STATUS ==
            setActiveRevision(status.revision || null);
            
            // 1. History inladen
            if (status.history && status.history.length > 0) {
                setChatHistory(status.history);
            }

            // 2. Bepaal Active Module op basis van backend response
            setActiveModule(status.active_module || "identity");

            // 3. Map backend phase naar frontend phase
            const phase = status.phase;
            
            if (phase === "IDENTITY") {
                if (status.needs_twin_review) {
                    // Laad twin data voor review
                    if (status.twin_data) {
                        setTwinReviewText(status.twin_data.narrative_profile || "");
                    }
                    setCurrentPhase("IDENTITY_REVIEW");
                } else if (!status.history || status.history.length === 0) {
                    // Geen history → interview nog niet gestart, start nu
                    await startInterview();
                } else {
                    setCurrentPhase("IDENTITY");
                }
            }
            else if (phase === "AMBITION") {
                if (status.needs_ambition_review) {
                    setTwinReviewText(status.ambition_text || "");
                    setCurrentPhase("AMBITION_REVIEW");
                } else if (!status.history || status.history.length === 0) {
                    // Geen history → ambitie-interview nog niet gestart, start nu
                    await startAmbitionModule();
                } else {
                    setActiveModule("ambition");
                    setCurrentPhase("AMBITION");
                }
            }
            else if (phase === "OCCUPATION") {
                setActiveModule("occupation");
                setCurrentPhase("OCCUPATION");
            }
            else if (phase === "SKILLS") {
                setActiveModule("skills");
                setCurrentPhase("SKILLS");
            }
            else if (phase === "CONSTRAINTS") {
                setActiveModule("constraints");
                setCurrentPhase("CONSTRAINTS");
            }
            else if (phase === "COMPLETED") {
                setAssessmentCompleted(true);
                setCurrentPhase("COMPLETED");
            }
        }
      } catch (e) {
        console.error("Restore error:", e);
      } finally {
        setLoadingState(false);
      }
    }
    restoreSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount


  // Functie voor "Pauzeren / Terug naar Dashboard"
  const handlePause = () => {
    // State is in de backend al opgeslagen bij elke chat message.
    // We hoeven alleen te navigeren.
    onBackToDashboard();
  };

  // Een herziening eindigt zodra het onderdeel opnieuw is afgerond.
  function finishRevision() {
    setActiveRevision(null);
    onBackToDashboard();
  }

  async function handleCancelRevision() {
    setBusy(true);
    try {
      await fetch(`${apiBase}/api/candidate/revision/cancel?candidateId=${encodeURIComponent(candidateId)}`, { method: "POST" });
      finishRevision();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  }

  // --- Render Steps ---

  const isProcessing = busy || isTyping;

  if (loadingState) {
      return (
        <div className="relative h-screen overflow-hidden bg-[#f6f1e8] text-[#151827]">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.10]"
            style={{
              backgroundImage: 'radial-gradient(#151827 1px, transparent 1px)',
              backgroundSize: '18px 18px',
            }}
          />
          <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-[#3f7f65] opacity-25 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 top-8 h-[28rem] w-[28rem] rounded-full bg-[#713d8d] opacity-25 blur-3xl" />
          <div className="relative z-10 flex h-screen items-center justify-center">
            <div className="inline-flex items-center gap-3 rounded-full border-2 border-[#151827] bg-white px-5 py-3 text-[#151827] shadow-[6px_6px_0_#151827]">
              <span className="h-5 w-5 animate-spin rounded-full border-[3px] border-[#3f7f65] border-t-transparent" />
              <span className="font-black">{t('candidate_app.loading.session')}</span>
            </div>
          </div>
        </div>
      );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f6f1e8] pb-20 font-sans text-[#151827] selection:bg-green-100 selection:text-green-900">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage: 'radial-gradient(#151827 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      />
      <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-[#3f7f65] opacity-25 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 top-8 h-[28rem] w-[28rem] rounded-full bg-[#713d8d] opacity-25 blur-3xl" />
      
      {/* Header Bar */}
      <header className="sticky top-0 z-10 border-b-4 border-[#151827] bg-white/95 shadow-sm backdrop-blur safe-top">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Top row: Logo + ID badge */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
               <img src="/logozondertekst.png" alt="Betaaldsolliciteren.nl" className="h-6 sm:h-8 w-auto flex-shrink-0" />
               <h1 className="truncate text-base font-black tracking-tight text-[#3f7f65] sm:text-xl">Betaaldsolliciteren.nl</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Candidate ID - smaller on mobile */}
              <div className="max-w-[80px] truncate rounded-full border-2 border-[#151827] bg-[#fffdf8] px-2 py-1 font-mono text-[10px] text-[#151827]/70 sm:max-w-none sm:px-3 sm:text-xs">
                {candidateId}
              </div>
              {/* PAUZE KNOP */}
              <button 
                onClick={handlePause}
                className="touch-target flex items-center gap-1 text-xs font-semibold text-[#151827]/70 hover:text-[#3f7f65] sm:text-sm"
              >
                <span className="hidden sm:inline">{t('candidate_app.header.save_and_stop')}</span>
                <span className="sm:hidden">✖</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-6 py-10">
        
      {activeRevision && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-[1.2rem] border-2 border-[#3f7f65] bg-[#eef6f2] px-4 py-3">
          <p className="text-sm font-bold text-[#151827]">
            {t('revision.banner', { section: t(`revision.sections.${activeRevision.section}`) })}
          </p>
          <button
            onClick={handleCancelRevision}
            disabled={busy}
            className="rounded-lg border-2 border-[#151827] bg-white px-3 py-1.5 text-xs font-bold text-[#151827] transition hover:bg-gray-50 disabled:opacity-50"
          >
            {t('revision.cancel')}
          </button>
        </div>
      )}

      {/* Progress Stepper - 6 fases */}
        <div className="mb-8 rounded-[1.2rem] border-2 border-[#151827]/20 bg-white/75 px-4 py-4 shadow-sm backdrop-blur">
          <div className="flex items-center justify-between text-xs font-medium">
            {[
              { key: "IDENTITY", label: t('candidate_app.progress.identity'), icon: "👤" },
              { key: "AMBITION", label: t('candidate_app.progress.ambition'), icon: "🎯" },
              { key: "OCCUPATION", label: t('candidate_app.progress.occupation'), icon: "💼" },
              { key: "SKILLS", label: t('candidate_app.progress.skills'), icon: "⚡" },
              { key: "CONSTRAINTS", label: t('candidate_app.progress.constraints'), icon: "⚖️" },
              { key: "COMPLETED", label: t('candidate_app.progress.completed'), icon: "✓" },
            ].map((phase, idx, arr) => {
              const phaseOrder = ["START", "IDENTITY", "AMBITION", "AMBITION_REVIEW", "OCCUPATION", "SKILLS", "CONSTRAINTS", "COMPLETED"];
              const currentIdx = phaseOrder.indexOf(currentPhase);
              const phaseIdx = phaseOrder.indexOf(phase.key);
              const isActive = currentPhase === phase.key || currentPhase === phase.key + "_REVIEW";
              const isComplete = phaseIdx < currentIdx;
              const isLast = idx === arr.length - 1;
              
              return (
                <React.Fragment key={phase.key}>
                  <div className="flex flex-col items-center">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm mb-1 transition-all
                      ${isComplete ? "bg-[#3f7f65] text-white" : isActive ? "bg-[#e9f4ef] text-[#3f7f65] ring-2 ring-[#3f7f65]" : "bg-gray-100 text-gray-400"}
                    `}>
                      {isComplete ? "✓" : phase.icon}
                    </div>
                    <span className={`hidden sm:block ${isActive ? "text-[#3f7f65] font-bold" : isComplete ? "text-[#3f7f65]" : "text-gray-400"}`}>
                      {phase.label}
                    </span>
                  </div>
                  {!isLast && (
                    <div className={`mx-1 h-0.5 flex-1 ${isComplete ? "bg-[#3f7f65]" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Terug naar website link */}
        {onBackToHome && (
          <div className="mb-4 text-center">
            <button 
              onClick={onBackToHome}
              className="text-sm text-gray-500 transition-colors hover:text-[#3f7f65]"
            >
              {t('candidate_app.back_to_website')}
            </button>
          </div>
        )}

        {/* START SCHERM */}
        {currentPhase === "START" && (
            <div className="animate-fade-in-up space-y-6 rounded-[1.5rem] border-4 border-[#151827] bg-white/90 p-6 shadow-[12px_12px_0_#151827]">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-black text-[#151827]">
                  {t('candidate_app.start.welcome', { name: personalia.name?.split(' ')[0] || t('candidate_app.start.candidate_fallback') })}
              </h2>
              <p className="text-gray-500 mt-2">
                  {t('candidate_app.start.profile_ready')}
              </p>
              <p className="mt-4 inline-block -rotate-1 rounded-xl border-2 border-[#151827] bg-[#fffdf8] px-4 py-2 text-sm font-bold text-[#151827] shadow-[4px_4px_0_#151827]">
                {t('candidate_app.start.help_hint')}
              </p>
            </div>
            
            {/* <Card className="space-y-6">
              // Blok 1: Contact & Basis 
              <div>
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 border-b pb-2">Contact & Rol</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Volledige Naam</Label>
                    <Input placeholder="Jouw naam" value={personalia.name} onChange={e => setPersonalia({...personalia, name: e.target.value})} />
                  </div>
                  <div>
                    <Label>E-mailadres</Label>
                    <Input placeholder="jouw@email.nl" value={personalia.email} onChange={e => setPersonalia({...personalia, email: e.target.value})} />
                  </div>
                  <div>
                    <Label>Telefoonnummer (optioneel)</Label>
                    <Input placeholder="06 12345678" value={personalia.phone} onChange={e => setPersonalia({...personalia, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label>Huidige Rol / Functie / Studie</Label>
                    <Input placeholder="Bijv. Student Informatica of Junior Developer" value={personalia.current_role} onChange={e => setPersonalia({...personalia, current_role: e.target.value})} />
                  </div>
                </div>
              </div>

              // Blok 2: Opleiding 
              <div>
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 border-b pb-2">Opleiding</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Hoogst afgerond niveau</Label>
                    <select 
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={personalia.education_level}
                      onChange={e => setPersonalia({...personalia, education_level: e.target.value})}
                    >
                      <option value="MBO">MBO</option>
                      <option value="HBO">HBO</option>
                      <option value="WO">WO</option>
                      <option value="PhD">PhD</option>
                      <option value="Anders">Anders</option>
                    </select>
                  </div>
                  <div>
                    <Label>Volg je nu een opleiding?</Label>
                    <select 
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={personalia.education_current}
                      onChange={e => setPersonalia({...personalia, education_current: e.target.value})}
                    >
                      <option value="Nee">Nee</option>
                      <option value="Ja, voltijd">Ja, voltijd</option>
                      <option value="Ja, deeltijd">Ja, deeltijd/cursus</option>
                    </select>
                  </div>
                </div>
                <div>
                   <Label>Welke opleidingen heb je gevolgd? (Korte samenvatting)</Label>
                   <textarea 
                     className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                     rows={2}
                     placeholder="Bijv: HBO Communicatie (Avans), Minor Psychology..."
                     value={personalia.education_history}
                     onChange={e => setPersonalia({...personalia, education_history: e.target.value})}
                   />
                </div>
              </div>

              //Blok 3: Context 
              <div>
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 border-b pb-2">Context</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Fase</Label>
                    <select 
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={personalia.career_phase}
                      onChange={e => setPersonalia({...personalia, career_phase: e.target.value})}
                    >
                      <option value="Studerend">Studerend</option>
                      <option value="Werkzoekend">Werkzoekend</option>
                      <option value="Werkend">Werkend</option>
                      <option value="Ondernemend">Ondernemend</option>
                    </select>
                  </div>
                  <div>
                    <Label>Regio / Land</Label>
                    <Input placeholder="Bijv. Utrecht, NL" value={personalia.location} onChange={e => setPersonalia({...personalia, location: e.target.value})} />
                  </div>
                  <div>
                    <Label>Leeftijd (optioneel)</Label>
                    <Input placeholder="Bijv. 23" value={personalia.age} onChange={e => setPersonalia({...personalia, age: e.target.value})} />
                  </div>
                </div>
              </div>

              //Blok 4: Online Context (P8/P9)
              <div>
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 border-b pb-2">Online Context & Privacy</h3>
                
                <div className="bg-green-50 p-4 rounded-lg border border-green-100 mb-4">
                  <div className="flex items-start gap-3">
                    <input 
                      type="checkbox" 
                      id="searchConsent"
                      className="mt-1 h-5 w-5 text-brand-green rounded focus:ring-brand-green cursor-pointer"
                      checked={personalia.online_search_consent}
                      onChange={e => setPersonalia({...personalia, online_search_consent: e.target.checked})}
                    />
                    <div>
                      <label htmlFor="searchConsent" className="font-semibold text-gray-900 cursor-pointer">
                        Ik geef toestemming om publieke online informatie te zoeken (P8)
                      </label>
                      <p className="text-xs text-gray-600 mt-1">
                        De AI zoekt alleen naar publieke bronnen (zoals LinkedIn) om je profiel context te geven. 
                        Er wordt niets opgeslagen buiten dit rapport.
                      </p>
                    </div>
                  </div>
                </div>

                {personalia.online_search_consent && (
                  <div className="animate-fade-in-up">
                    <Label>Zijn er bronnen die we wel/niet mogen gebruiken? (P9)</Label>
                    <Input 
                      placeholder="Bijv: 'Mijn LinkedIn mag wel, maar negeer mijn persoonlijke blog.'" 
                      value={personalia.online_search_restrictions} 
                      onChange={e => setPersonalia({...personalia, online_search_restrictions: e.target.value})} 
                    />
                  </div>
                )}
              </div>

              <div className="pt-4 flex justify-end">
                <Button onClick={handlePersonalia} disabled={busy}>
                  {busy && personalia.online_search_consent 
                    ? "Online Profiel Scannen..." 
                    : busy 
                      ? "Laden..." 
                      : "Opslaan & Start Interview"}
                </Button>
              </div>
            </Card> */}
            {/* NIEUWE UI: ALLEEN EEN START KNOP */}
            <div className="flex justify-center">
                <button 
                    onClick={handleStartIntake} 
                    disabled={busy}
                    className="rounded-xl bg-[#3f7f65] px-8 py-4 text-lg font-black text-white transition-transform hover:translate-x-0.5 hover:translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ boxShadow: '8px 8px 0 #151827' }}
                >
                  {busy ? t('candidate_app.start.preparing') : t('candidate_app.start.start_button')}
                </button>
            </div>
          </div>
        )}

        {/* IDENTITY REVIEW SCHERM */}
        {currentPhase === "IDENTITY_REVIEW" && (
          <div className="animate-fade-in-up flex-1 overflow-y-auto rounded-[1.5rem] border-4 border-[#151827] bg-white/90 p-4 shadow-[12px_12px_0_#151827] scroll-touch scrollbar-hide md:p-8">
            <div className="max-w-3xl mx-auto space-y-8">
              
              {/* Header */}
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#e9f4ef] text-2xl text-[#3f7f65]">
                  ✏️
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('candidate_app.identity_review.title')}</h2>
                <p className="text-gray-500">
                  {t('candidate_app.identity_review.subtitle')}
                </p>
              </div>

              {/* SECTIE 1: Narrative Profile (Tekst) */}
              <div className="rounded-xl border-2 border-[#151827]/20 bg-[#fffdf8] p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4 border-b pb-2">
                  <span className="text-xl">📄</span>
                  <h3 className="font-bold text-gray-800">{t('candidate_app.identity_review.story_heading')}</h3>
                </div>
                <TextArea 
                  value={twinReviewText}
                  onChange={(e) => setTwinReviewText(e.target.value)}
                                className="min-h-[250px] border-2 border-[#151827]/20 bg-white font-mono text-sm leading-relaxed transition-colors focus:border-[#3f7f65] focus:bg-white"
                            />
                        </div>

                        {/* KISS: SECTIE 2 & 3 (Constraints en Non-Negotiables) verwijderd - voegen weinig toe in de praktijk
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center gap-2 mb-4 border-b pb-2">
                                <span className="text-xl">⚖️</span>
                                <h3 className="font-bold text-gray-800">Randvoorwaarden</h3>
                            </div>
                            <p className="text-xs text-gray-500 mb-4">
                                Klik op een blokje om het aan (Groen) of uit (Grijs) te zetten.
                            </p>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {Object.entries(twinConstraints).map(([key, value]) => (
                                    <div 
                                        key={key}
                                        onClick={() => toggleTwinConstraint(key)}
                                        className={`
                                            cursor-pointer select-none p-3 rounded-lg border flex justify-between items-center transition-all
                                            ${value 
                                                ? "bg-green-50 border-green-200 text-green-900 shadow-sm" 
                                                : "bg-gray-50 border-gray-200 text-gray-400 grayscale"
                                            }
                                        `}
                                    >
                                        <span className="font-medium text-sm capitalize">
                                            {key.replace(/_/g, " ")}
                                        </span>
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${value ? "bg-green-500 text-white" : "bg-gray-300 text-white"}`}>
                                            {value ? "✓" : "✕"}
                                        </div>
                                    </div>
                                ))}
                                {Object.keys(twinConstraints).length === 0 && (
                                    <p className="text-sm text-gray-400 italic">Geen randvoorwaarden gedetecteerd.</p>
                                )}
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center gap-2 mb-4 border-b pb-2">
                                <span className="text-xl">🔒</span>
                                <h3 className="font-bold text-gray-800">Harde Eisen (Non-Negotiables)</h3>
                            </div>
                            
                            <div className="flex gap-2 mb-4">
                                <Input 
                                    placeholder="Nieuwe harde eis toevoegen..." 
                                    value={newNonNegotiableInput}
                                    onChange={e => setNewNonNegotiableInput(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && addNonNegotiable()}
                                />
                                <Button onClick={addNonNegotiable} variant="secondary" className="shrink-0">
                                    + Toevoegen
                                </Button>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {twinNonNegotiables.map((item, idx) => (
                                    <span key={idx} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 text-red-700 border border-red-100 text-sm font-medium">
                                        {item}
                                        <button 
                                            onClick={() => removeNonNegotiable(idx)}
                                            className="w-4 h-4 flex items-center justify-center rounded-full bg-red-200 hover:bg-red-300 text-red-800 text-xs leading-none transition-colors"
                                        >
                                            ✕
                                        </button>
                                    </span>
                                ))}
                                {twinNonNegotiables.length === 0 && (
                                    <p className="text-sm text-gray-400 italic">Geen harde eisen ingesteld.</p>
                                )}
                            </div>
                        </div>
                        */}

                        {/* Footer Action */}
                        <div className="flex justify-end pt-4 pb-10">
                            <button 
                                onClick={handleConfirmTwin} 
                                disabled={busy} 
                              className="rounded-xl bg-[#3f7f65] px-10 py-4 text-lg font-black text-white transition-transform hover:translate-x-0.5 hover:translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                                style={{ boxShadow: '8px 8px 0 #151827' }}
                            >
                                {busy ? t('candidate_app.identity_review.processing') : t('candidate_app.identity_review.confirm_continue')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        {/* CHAT-BASED FASES: IDENTITY, AMBITION, OCCUPATION, SKILLS, CONSTRAINTS */}
        {["IDENTITY", "AMBITION", "OCCUPATION", "SKILLS", "CONSTRAINTS", "COMPLETED"].includes(currentPhase) && (
            <div className="flex h-[75vh] flex-col safe-bottom">
                <>
                  {/* Chat History */}
              <div className="mb-4 flex-1 space-y-4 overflow-y-auto rounded-[1.2rem] border-2 border-[#151827]/20 bg-white/85 p-4 shadow-inner no-overscroll scroll-touch scrollbar-hide">
                    {chatHistory.map((msg, i) => (
                      <ChatMessage 
                          key={i} 
                          role={msg.role} 
                          text={msg.text}
                          widget_type={msg.widget_type}  // Belangrijk: doorgeven!
                          widget_data={msg.widget_data}  // Belangrijk: doorgeven!
                          onAction={handleChatAction}    // De nieuwe handler doorgeven
                          isLatest={i === chatHistory.length - 1}
                      />
                    ))}

                    {isTyping && (
                      <div className="flex justify-start animate-pulse">
                        <div className="rounded-2xl rounded-tl-none border border-gray-200 bg-white px-4 py-2 text-xs italic text-gray-400 shadow-sm">
                          {t('candidate_app.chat.thinking')}
                        </div>
                      </div>
                    )}
                    
                    {/* Toon eindrapport onderaan de chat als het klaar is */}
                    {finalReport && (
                      <div className="mt-8">
                          {/* Je bestaande rapport component kan hier blijven */}
                          <FinalReportAndReflection 
                            report={finalReport}
                            // ... je bestaande props doorgeven ...
                            reflectionText={reflectionText} setReflectionText={setReflectionText}
                            learningGoals={learningGoals} setLearningGoals={setLearningGoals}
                            roleFitness={roleFitness}           // <--- Doorreiken
                            setRoleFitness={setRoleFitness}     // <--- Doorreiken
                            reflectionSubmitted={reflectionSubmitted} onSubmitReflection={submitReflection}
                            reflectionSubmitting={reflectionSubmitting}
                          />
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input Bar (Verberg als rapport er is, assessment klaar is, OF er een widget met eigen input actief is) */}
                  {!finalReport && !assessmentCompleted && (() => {
                    // Check of laatste bericht een widget heeft die eigen input verwacht
                    const lastMsg = chatHistory[chatHistory.length - 1];
                    const widgetsWithOwnInput = ["skills_intro", "skills_case", "skill_question", "constraints_selector", "occupation_selector", "direction_selector"];
                    const hasActiveWidget = lastMsg?.widget_type && widgetsWithOwnInput.includes(lastMsg.widget_type);
                    return !hasActiveWidget;
                  })() && (
                    <Card className="sticky bottom-2 border-2 border-[#151827]/20 p-3 !rounded-2xl shadow-lg">
                      <div className="flex gap-2">
                        <textarea
                          className="h-14 flex-1 resize-none rounded-lg border-2 border-[#151827]/30 px-4 py-3 text-sm focus:border-[#3f7f65] focus:outline-none focus:ring-0"
                          placeholder={t('candidate_app.chat.input_placeholder')}
                          value={userInput}
                          onChange={(e) => setUserInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleChatAction("message", userInput);
                            }
                          }}
                          disabled={isTyping}
                        />
                        <Button 
                          onClick={() => handleChatAction("message", userInput)} 
                          disabled={!userInput.trim()}
                          loading={isTyping}
                          className="h-14 w-14 !p-0 rounded-full"
                        >
                          ➤
                        </Button>
                      </div>
                    </Card>
                  )}
                </>
              </div>
            )}

        {/* AMBITION REVIEW SCHERM */}
        {currentPhase === "AMBITION_REVIEW" && (
          <div className="animate-fade-in-up flex h-[80vh] flex-col overflow-y-auto rounded-[1.5rem] border-4 border-[#151827] bg-white/90 p-6 shadow-[12px_12px_0_#151827]">
            <div className="max-w-3xl mx-auto w-full space-y-6">
                <div className="text-center mb-6">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#e9f4ef] text-2xl text-[#3f7f65]">
                        🎯
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('candidate_app.ambition_review.title')}</h2>
                  <p className="text-gray-500">{t('candidate_app.ambition_review.subtitle')}</p>
                </div>
                <div className="rounded-xl border-2 border-[#151827]/20 bg-[#fffdf8] p-6 shadow-sm">
                    <TextArea 
                        value={twinReviewText}
                        onChange={(e) => setTwinReviewText(e.target.value)}
                    className="min-h-[250px] border-2 border-[#151827]/20 bg-white font-mono text-sm leading-relaxed focus:border-[#3f7f65]"
                    />
                </div>
                <div className="flex justify-end pt-4">
                    <button 
                        onClick={handleConfirmAmbition} 
                        disabled={busy}
                      className="rounded-xl bg-[#3f7f65] px-8 py-4 text-lg font-black text-white transition-transform hover:translate-x-0.5 hover:translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ boxShadow: '8px 8px 0 #151827' }}
                    >
                        {busy ? `${t('common.save')}...` : t('candidate_app.ambition_review.confirm_choose_occupation')}
                    </button>
                </div>
            </div>
          </div>
        )}

        {/* Oude step logica verwijderd - alles gaat nu via chat met widgets */}
        
      </main>

      {isProcessing && (
        <div className="pointer-events-none fixed bottom-5 right-5 z-50">
          <div className="inline-flex items-center gap-2 rounded-full border-2 border-[#151827] bg-white px-3 py-2 text-xs font-bold text-[#151827] shadow-[4px_4px_0_#151827]">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#3f7f65] border-t-transparent" />
            {t('candidate_app.processing_badge')}
          </div>
        </div>
      )}

      {/* Pop-up bij volledige afronding van de intake */}
      {assessmentCompleted && (
        <IntakeCompleteModal
          accentColor="#3f7f65"
          title={t('candidate_app.completion_modal.title')}
          thankYouText={t('candidate_app.completion_modal.thank_you')}
          nextStepsTitle={t('candidate_app.completion_modal.next_steps_title')}
          nextStepsBody={t('candidate_app.completion_modal.next_steps_body')}
          buttonLabel={t('candidate_app.completion_modal.button')}
          onClose={() => {
            setAssessmentCompleted(false);
            if (onBackToDashboard) {
              onBackToDashboard();
            } else {
              setCurrentPhase("START");
            }
          }}
        />
      )}
    </div>
  );
}

// --- FINAL REPORT COMPONENT ---
function FinalReportAndReflection({ 
  report, 
  reflectionText, setReflectionText, 
  learningGoals, setLearningGoals, 
  roleFitness, setRoleFitness,
  reflectionSubmitted, onSubmitReflection 
}) {
  const { t } = useTranslation();
  
  // Dummy handlers voor de toekomst
  const handleNewRole = () => alert(t('candidate_app.final.todo_new_role'));
  const handleOpportunities = () => alert(t('candidate_app.final.todo_opportunities'));

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-[85vh] overflow-hidden">
      
      {/* --- LINKER PANEEL: HET RAPPORT (Scrollable) --- */}
      <div className="flex-1 overflow-y-auto pr-2 space-y-6 scrollbar-hide">
         
         {/* Header Card */}
         <Card className="bg-brand-green text-white border-none">
            <h2 className="text-2xl font-bold mb-2">{t('candidate_app.final.profile_title')}</h2>
            <p className="text-green-200 text-sm">
              {t('candidate_app.final.based_on_role')} <span className="font-bold text-white">{report.chosen_occupation?.label}</span>
            </p>
         </Card>

         {/* AI Samenvatting */}
         {report.final_report?.summary && (
           <Card className="bg-white border-green-100">
              <h3 className="text-sm font-bold text-gray-400 uppercase mb-2">{t('candidate_app.final.ai_summary')}</h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                {report.final_report.summary}
              </p>
           </Card>
         )}

         {/* Skills Per Groep */}
         {report.groups?.map((group) => (
           <div key={group.group_uri} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
             {/* Groep Header */}
             <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
               <h3 className="font-bold text-gray-800">{group.group_label}</h3>
             </div>
             
             {/* Skills List */}
             <div className="divide-y divide-gray-100">
               {group.skills.map((skill) => (
                 <div key={skill.skill_uri} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <span className="text-sm text-gray-700 font-medium">{skill.skill_label}</span>
                    <div className="flex items-center gap-3">
                       {/* Visuele balk */}
                       <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-brand-green rounded-full" 
                            style={{ width: `${(skill.score / 5) * 100}%` }}
                          ></div>
                       </div>
                       <span className="text-sm font-bold text-gray-900 w-6 text-right">{skill.score}</span>
                    </div>
                 </div>
               ))}
               {group.skills.length === 0 && (
                 <div className="px-5 py-3 text-xs text-gray-400 italic">Geen skills in deze groep.</div>
               )}
             </div>
           </div>
         ))}
      </div>


      {/* --- RECHTER PANEEL: REFLECTIE (Sticky) --- */}
      <div className="lg:w-[400px] shrink-0 flex flex-col">
        <Card className="h-full bg-gray-900 text-white border-gray-800 flex flex-col">
          
          <div className="mb-6">
            <h3 className="text-xl font-bold mb-1">{t('candidate_app.final.reflection_title')}</h3>
            <p className="text-gray-400 text-xs">{t('candidate_app.final.reflection_hint')}</p>
          </div>

          {/* MODE 1: FORMULIER (Nog niet gesubmit) */}
          {!reflectionSubmitted ? (
            <div className="flex-1 flex flex-col space-y-6 overflow-y-auto pr-1">
              
              {/* Vraag 1 */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">{t('candidate_app.final.what_stands_out')}</label>
                <textarea 
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none resize-none"
                  rows={4}
                  placeholder={t('candidate_app.final.what_stands_out_placeholder')}
                  value={reflectionText}
                  onChange={(e) => setReflectionText(e.target.value)}
                />
              </div>

              {/* Vraag 2 */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">{t('candidate_app.final.learning_goals')}</label>
                <textarea 
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none resize-none"
                  rows={4}
                  placeholder={t('candidate_app.final.learning_goals_placeholder')}
                  value={learningGoals}
                  onChange={(e) => setLearningGoals(e.target.value)}
                />
              </div>

              {/* Vraag 3: De Nieuwe Slider */}
              <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <label className="block text-xs font-bold text-gray-400 uppercase mb-4">
                  {t('candidate_app.final.role_fit')}
                </label>
                <p className="text-xs text-gray-300 mb-4 leading-relaxed">
                  {t('candidate_app.final.role_fit_body', { role: report.chosen_occupation?.label })}
                </p>
                
                <div className="flex justify-between text-xs text-gray-500 mb-2 font-bold">
                  <span>{t('candidate_app.final.role_fit_min')}</span>
                  <span>{t('candidate_app.final.role_fit_max')}</span>
                </div>
                <input 
                  type="range" 
                  min="1" max="10" 
                  value={roleFitness}
                  onChange={(e) => setRoleFitness(Number(e.target.value))}
                  className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-brand-green"
                />
                <div className="text-center mt-2 font-bold text-green-400 text-xl">
                  {roleFitness}/10
                </div>
              </div>

              <div className="pt-4 mt-auto">
                <Button 
                  onClick={onSubmitReflection} 
                  className="w-full bg-white text-black hover:bg-gray-200 border-none font-bold"
                >
                  {t('candidate_app.final.save_finish')}
                </Button>
              </div>
            </div>
          ) : (
            
            /* MODE 2: KLAAR (Knoppen tonen) */
            <div className="flex-1 flex flex-col justify-center items-center space-y-6 animate-fade-in-up">
              <div className="w-16 h-16 bg-green-500 text-black rounded-full flex items-center justify-center text-3xl mb-2">
                ✔
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-white">{t('candidate_app.final.saved_title')}</h3>
                <p className="text-gray-400 text-sm mt-2">{t('candidate_app.final.saved_hint')}</p>
              </div>

              <div className="w-full space-y-3 pt-6">
                <button 
                  onClick={handleNewRole}
                  className="w-full py-4 px-6 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-xl text-left transition-all group"
                >
                  <span className="block text-xs font-bold text-gray-400 uppercase mb-1">{t('candidate_app.final.option_one')}</span>
                  <span className="font-bold text-white group-hover:text-green-300 transition-colors">{t('candidate_app.final.choose_new_role')}</span>
                  <span className="block text-xs text-gray-500 mt-1">{t('candidate_app.final.choose_new_role_body')}</span>
                </button>

                <button 
                  onClick={handleOpportunities}
                  className="w-full py-4 px-6 bg-brand-green hover:opacity-90 border border-brand-green rounded-xl text-left transition-all shadow-lg shadow-green-900/50"
                >
                  <span className="block text-xs font-bold text-green-200 uppercase mb-1">{t('candidate_app.final.option_two')}</span>
                  <span className="font-bold text-white text-lg">{t('candidate_app.final.generate_opportunities')}</span>
                  <span className="block text-xs text-green-100 mt-1">{t('candidate_app.final.generate_opportunities_body')}</span>
                </button>
              </div>
            </div>
          )}

        </Card>
      </div>
    </div>
  );
}